import { Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import Select from "react-select";
import AddImageComponent from "../activity/AddImageComponent";
import InputMask from "react-input-mask";
import * as yup from "yup";
import { useFormik } from "formik";
import { useContacts } from "../../state/ContactContext";
import { useEffect, useState } from "react";
import CancelModal from "./CancelModal";

const AddEditContactDrawer = ({ showAddContactDrawer, handleClose }) => {
  const { selectedContact, addContact, updateContact, resendInvite } =
    useContacts();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState(null);

  const contactTypeOptions = [
    { label: "Regular and emergency", value: ["regular", "emergency"] },
    { label: "Regular", value: ["regular"] },
    { label: "Emergency", value: ["emergency"] },
  ];

  const schema = yup.object().shape({
    firstName: yup.string().required("Enter first name."),
    lastName: yup.string().required("Enter last name."),
    email: yup.string().email("Enter a valid email.").required("Enter email."),
    phone: yup
      .string()
      .matches(/^\+1\d{10}$/, "Phone number must be in the format +1XXXXXXXXXX")
      .required("Enter valid U.S phone number."),
    contactTypes: yup.mixed().required("Select contact type."),
    photo: yup.string().nullable(),
  });

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    contactTypes: [],
    photo: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const emailError = await (selectedContact
        ? updateContact(selectedContact.id, { ...selectedContact, ...values })
        : addContact({ ...selectedContact, ...values }));

      if (emailError) {
        formik.setFieldError("email", emailError);
        return;
      }

      handleClose();
      formik.resetForm({ values: initialValues });
    },
  });

  useEffect(() => {
    if (selectedContact) {
      formik.resetForm({
        values: {
          firstName:
            selectedContact?.firstName || selectedContact?.first_name || "",
          lastName:
            selectedContact?.lastName || selectedContact?.last_name || "",
          email: selectedContact.email || "",
          phone: selectedContact.phone || "",
          contactTypes: selectedContact.contactTypes || [],
          photo: selectedContact.image || "",
        },
      });
    } else {
      formik.resetForm({
        values: initialValues,
      });
    }
  }, [selectedContact]);

  const handleResendInvite = async () => {
    if (!selectedContact?.email) {
      setResendMessage({ type: "error", text: "Contact email is required" });
      return;
    }

    const firstName = selectedContact?.firstName || selectedContact?.first_name;
    const lastName = selectedContact?.lastName || selectedContact?.last_name;
    if (!firstName || !lastName) {
      setResendMessage({
        type: "error",
        text: "Contact must have first name and last name before sending invitation. Please update the contact first.",
      });
      setTimeout(() => setResendMessage(null), 5000);
      return;
    }

    setIsResending(true);
    setResendMessage(null);

    const result = await resendInvite(
      selectedContact.email,
      firstName,
      lastName
    );

    if (result.success) {
      setResendMessage({
        type: "success",
        text: `Invitation sent to ${selectedContact.email}`,
      });
    } else {
      setResendMessage({
        type: "error",
        text: result.message || "Failed to resend invitation",
      });
    }

    setIsResending(false);

    // Clear message after 5 seconds
    setTimeout(() => setResendMessage(null), 5000);
  };

  const isUnregistered =
    selectedContact &&
    selectedContact.contactRole === "contact" &&
    selectedContact.status !== "active";

  if (!showAddContactDrawer) {
    return;
  }

  return (
    <div
      className={`page-drawer-modal ${showAddContactDrawer && "show-drawer"}`}
    >
      <CancelModal
        cancelOpen={cancelOpen}
        handleCancelClose={() => setCancelOpen(false)}
        handleLeave={() => {
          setCancelOpen(false);
          handleClose();
        }}
        modalTitle={"Leave without saving?"}
        modalDescription={"You’ll lose any changes you made to this contact."}
      />

      <div className="page-drawer-container">
        <div className="pd-header">
          <h4>
            {selectedContact
              ? `Edit Contact: ${
                  selectedContact?.firstName ||
                  selectedContact?.first_name ||
                  ""
                } ${
                  selectedContact?.lastName || selectedContact?.last_name || ""
                }`
              : "Add New Contact"}
          </h4>
        </div>
        <div className="pd-content">
          <div className="new-contact-container">
            <h6 className="form-title">Contact Details</h6>
            <form onSubmit={formik.handleSubmit} className="inline-form">
              <div className="form-group">
                <label htmlFor="firstName">First Name</label>
                <div className="input-grp">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    className="form-control"
                    placeholder="First Name"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.firstName && formik.errors.firstName && (
                    <small className="text-danger">
                      {formik.errors.firstName}
                    </small>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="lastName">Last Name</label>

                <div className="input-grp">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    className="form-control"
                    placeholder="Last Name"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.lastName && formik.errors.lastName && (
                    <small className="text-danger">
                      {formik.errors.lastName}
                    </small>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>

                <div className="input-grp">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    placeholder="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!!selectedContact}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <small className="text-danger">{formik.errors.email}</small>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contactType">
                  <Tooltip title="Regular contacts can be contacted by your client one-by-one at any time. Emergency contacts can be contacted by your client in a repeated loop until one of them picks up.">
                    <InfoIcon />
                  </Tooltip>
                  Contact Type
                </label>
                <Select
                  name="contactType"
                  options={contactTypeOptions}
                  className="form-control select-field"
                  classNamePrefix="select"
                  placeholder="Select contact type"
                  value={
                    contactTypeOptions.find((opt) =>
                      opt.value.every((val) =>
                        formik.values.contactTypes.includes(val)
                      )
                    ) || null
                  }
                  onChange={(option) =>
                    formik.setFieldValue("contactTypes", option?.value || [])
                  }
                  onBlur={() => formik.setFieldTouched("contactTypes", true)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <div className="input-grp">
                  <InputMask
                    mask="+19999999999"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {(inputProps) => (
                      <input
                        {...inputProps}
                        type="tel"
                        id="phone"
                        name="phone"
                        className="form-control"
                        placeholder="+1XXXXXXXXXX"
                      />
                    )}
                  </InputMask>
                  {formik.touched.phone && formik.errors.phone && (
                    <small className="text-danger">{formik.errors.phone}</small>
                  )}
                </div>
              </div>

              <div className="form-group with-image-field">
                <label htmlFor="photo">Select Image</label>
                <div className="image-component">
                  <AddImageComponent
                    inputName="photo"
                    fileName={formik.values.photo}
                    onChange={(fileName) =>
                      formik.setFieldValue("photo", fileName)
                    }
                  />
                </div>
              </div>

              {isUnregistered && (
                <div className="form-group resend-invite-section">
                  <button
                    type="button"
                    className="resend-invite-btn"
                    onClick={handleResendInvite}
                    disabled={isResending}
                  >
                    {isResending ? "Sending..." : "📧 Resend Invitation"}
                  </button>
                  {resendMessage && (
                    <small
                      className={`resend-message ${
                        resendMessage.type === "success"
                          ? "text-success"
                          : "text-danger"
                      }`}
                    >
                      {resendMessage.text}
                    </small>
                  )}
                </div>
              )}

              <div className="pd-footer">
                <div className="filter-btns-group">
                  <button
                    type="button"
                    onClick={() => {
                      if (formik.dirty) {
                        setCancelOpen(true);
                      } else {
                        handleClose();
                      }
                    }}
                  >
                    Cancel
                  </button>
                  <button type="submit">Save</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditContactDrawer;
